import { useEditorData, ZoomAnchor } from '../providers/EditorProvider';

export const ZOOM_PRESETS = [0.1, 0.25, 0.33, 0.5, 0.67, 0.75, 1, 1.25, 1.5, 2];
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 2;
// Browsers refuse or slow down on very large canvases; the canvas grows
// with the zoom (and the screen's pixel ratio).
const MAX_CANVAS_PIXELS = 8192;

export const getZoomLimits = (canvas: fabric.Canvas) => {
  const zoom = canvas.getZoom() || 1;
  const pixelRatio = window.devicePixelRatio || 1;
  const slideWidth = canvas.getWidth() / zoom;
  const slideHeight = canvas.getHeight() / zoom;
  const max = Math.min(
    MAX_ZOOM,
    MAX_CANVAS_PIXELS / (slideWidth * pixelRatio),
    MAX_CANVAS_PIXELS / (slideHeight * pixelRatio),
  );
  return { min: MIN_ZOOM, max: Math.max(MIN_ZOOM, max) };
};

export const useZoom = () => {
  const { canvas, zoomScale, setZoomScale, appliedZoom, zoomAnchorRef } =
    useEditorData();

  const zoomTo = (scale: number | null, anchor?: ZoomAnchor) => {
    if (!canvas) return;
    if (scale === null) {
      zoomAnchorRef.current = null;
      setZoomScale(null);
      return;
    }
    const { min, max } = getZoomLimits(canvas);
    zoomAnchorRef.current = anchor ?? null;
    setZoomScale(Math.min(max, Math.max(min, scale)));
  };

  const step = (direction: 1 | -1) => {
    const current = canvas?.getZoom() || appliedZoom;
    const presets = direction > 0 ? ZOOM_PRESETS : [...ZOOM_PRESETS].reverse();
    const next = presets.find((preset) =>
      direction > 0 ? preset > current * 1.01 : preset < current * 0.99,
    );
    if (next !== undefined) zoomTo(next);
  };

  return {
    zoom: appliedZoom,
    isFit: zoomScale === null,
    zoomTo,
    zoomIn: () => step(1),
    zoomOut: () => step(-1),
    zoomToFit: () => zoomTo(null),
  };
};
